import AccessTimeIcon from "@mui/icons-material/AccessTime"
import RotateRightIcon from "@mui/icons-material/RotateRight"
import { Box } from "@mui/material"
import React, { useMemo } from "react"
import { getLabelText, SeuilEnum } from "../../../core/enum/beem-shot/SeuilEnum"
import { CalculStatusEnum, calculStatusToLabel } from "../../../core/enum/calculStatusEnum"
import { displayDecimal, truncateTextCss } from "../../../core/services/helper-service"
import { DefaultTypography } from "../../typography/default-typography"

interface IProps {
  diffIc2022: number
  diffIc2025: number
  diffIc2028: number
  diffIc2031: number
  higherSeuilValide: SeuilEnum
  calculStatus: CalculStatusEnum
}

export function BSCalculStatusChip({
  diffIc2022,
  diffIc2025,
  diffIc2028,
  diffIc2031,
  higherSeuilValide,
  calculStatus,
}: Readonly<IProps>): React.JSX.Element {
  const seuilValide = useMemo(() => higherSeuilValide === SeuilEnum.INVALIDE, [higherSeuilValide])

  function getSeuilBackgroundColor(): string {
    switch (calculStatus) {
      case CalculStatusEnum.WAITING_FOR_MORPHOLOGY:
      case CalculStatusEnum.WAITING_FOR_CONFIGURATION:
      case CalculStatusEnum.CALCUL_IN_PROGRESS:
        return "#FFF3E0"
      case CalculStatusEnum.READY:
        return seuilValide ? "#FEEBEE" : "#E5FFF4"
      default:
        return "#FFF3E0"
    }
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight={10}
      borderRadius={2}
      sx={{ backgroundColor: getSeuilBackgroundColor(), pl: 2, pr: 2 }}>
      <ChipContent
        calculStatus={calculStatus}
        diffIc2022={diffIc2022}
        diffIc2025={diffIc2025}
        diffIc2028={diffIc2028}
        diffIc2031={diffIc2031}
        higherSeuilValide={higherSeuilValide}
        seuilValide={seuilValide}
      />
    </Box>
  )
}

interface IChipContentProps {
  diffIc2022: number
  diffIc2025: number
  diffIc2028: number
  diffIc2031: number
  higherSeuilValide: SeuilEnum
  calculStatus: CalculStatusEnum
  seuilValide: boolean
}

function ChipContent({
  diffIc2022,
  diffIc2025,
  diffIc2028,
  diffIc2031,
  calculStatus,
  higherSeuilValide,
  seuilValide,
}: Readonly<IChipContentProps>): React.JSX.Element {
  function getSeuil(seuil: SeuilEnum): string {
    switch (seuil) {
      case SeuilEnum.INVALIDE:
      case SeuilEnum.SEUIL_2022:
        return displayDecimal(Math.abs(diffIc2022))
      case SeuilEnum.SEUIL_2025:
        return displayDecimal(Math.abs(diffIc2025))
      case SeuilEnum.SEUIL_2028:
        return displayDecimal(Math.abs(diffIc2028))
      case SeuilEnum.SEUIL_2031:
        return displayDecimal(Math.abs(diffIc2031))
      default:
        return displayDecimal(0.0)
    }
  }

  switch (calculStatus) {
    case CalculStatusEnum.WAITING_FOR_MORPHOLOGY:
    case CalculStatusEnum.WAITING_FOR_CONFIGURATION:
      return (
        <>
          <RotateRightIcon fontSize="medium" sx={{ color: "#E65100", pr: 1 }} />
          <DefaultTypography
            label={calculStatusToLabel(calculStatus)}
            fontSize="11px"
            lineHeight="20.02px"
            fontWeight={400}
            color="#E65100"
            sx={truncateTextCss}
          />
        </>
      )

    case CalculStatusEnum.CALCUL_IN_PROGRESS:
      return (
        <>
          <AccessTimeIcon fontSize="medium" sx={{ color: "#E65100", pr: 1 }} />
          <DefaultTypography
            label={calculStatusToLabel(calculStatus)}
            fontSize="11px"
            lineHeight="20.02px"
            fontWeight={400}
            color="#E65100"
            sx={truncateTextCss}
          />
        </>
      )
    case CalculStatusEnum.READY:
      return (
        <DefaultTypography
          label={`${getLabelText(higherSeuilValide)} (${higherSeuilValide ? "+" : ""}${getSeuil(higherSeuilValide)}%)`}
          fontSize="11px"
          lineHeight="20.02px"
          fontWeight={400}
          color={seuilValide ? "#F00C0C" : "#009069"}
          sx={truncateTextCss}
        />
      )
    default:
      return (
        <>
          <RotateRightIcon fontSize="medium" sx={{ color: "#E65100", pr: 1 }} />
          <DefaultTypography
            label="En attente du calcul"
            fontSize="11px"
            lineHeight="20.02px"
            fontWeight={400}
            color="#E65100"
            sx={truncateTextCss}
          />
        </>
      )
  }
}
